import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  Renderer2,
  RendererStyleFlags2,
  OnInit,
  OnDestroy,
  HostListener,
  PLATFORM_ID,
  inject,
  NgZone
} from '@angular/core';
import {
  Router,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
} from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import DOMPurify from 'dompurify';
import { Application } from '@enums/application.enums';
import { CommonService } from '@modules/shared/services/common.service';
import { API_URLS } from '@core/api-url.enum';
import { AuthService } from '@core/auth.service';
import { ConnectionService } from '@services/connection.service';
import { Fields, HISTORY_NOT_TO_INCLUDE } from '@constants/constant';
import { SeoService } from '@services/seo.service';
import { environment } from '@environments/environment';
import { HistoryService } from '@core/routing-history.service';
import { ApiCallService } from '@services/api-call.service';
import { FirebaseService } from '@modules/shared/services/firebase.service';
import { DataService } from '@services/data.service';
import { imageSize } from '@constants/manifest.constant';
import { HeaderFooterStateService } from '@modules/shared/services/header-footer-state.service';
import { MarketingPageStateService } from '@modules/shared/services/marketing-page-state.service';
import { PromotionalMarketingCleanupService } from '@core/promotional-marketing-cleanup.service';
import { PwaService } from '@core/pwa.service';
import { IndexedDbService } from '@services/indexed-db.service';
import { MobileBridgeService } from '@services/mobile-bridge.service';
import { MOBILE_BRIDGE_EVENT_SEND } from '@constants/mobile-bridge.constant';
import { PlatformService } from '@core/services/platform.service';
import { LazyCssLoaderService } from '@core/services/lazy-css-loader.service';
import { extractWhiteLabelConfig, isValidWhiteLabelConfig } from '@utils/common.utils';
import { isCoverPageShellPath } from '@modules/sub-page-view/sub-page-view.constants';
import { WLS_PREVIEW_MESSAGE_TYPE } from '@modules/edit-preference/components/whitelabel-site/whitelabel-site-constant';
import { CanonicalService } from '@services/canonical.service';
import { GtagService } from '@services/gtag.service';
import { LocaleService } from '@core/i18n/locale.service';
import { ROUTE_PATHS } from '@constants/routes';
import { AUTH_CONSTANTS } from '@modules/auth/constants/authConstants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  // Component state
  private readonly destroy$ = new Subject<void>();
  private readonly inputSanitize$ = new Subject<{ target: HTMLInputElement | HTMLTextAreaElement }>();
  public loadingDataImg = false;
  public readonly Fields: typeof Fields = Fields;
  public isBrowser: boolean;
  private globalClickListener?: () => void;
  private previewMessageHandler?: (event: MessageEvent) => void;
  public readonly authService: AuthService = inject(AuthService);
  public showSidebar: boolean = false;
  private showSidebarForRoute = false;
  private isMarketingPageVisible = false;
  private isSeoLandingPageVisible = false;
  public isWhiteLabelCoverPageVisible = false;
  public isCoverPageShellRoute = false;
  public isEmbedWLSMode: boolean = false;
  public previewLogoUrl: string = '';
  public previewIsTextLogo = false;
  public readonly showDevToolbar = !environment.production;
  readonly wlsPreviewHeaderLabels = {
    GET_STARTED: AUTH_CONSTANTS.GET_STARTED
  } as const;
  readonly loginPath = ROUTE_PATHS.AUTH_LOGIN;
  private whitelabelDataFetched = false;

  /**
   * Sanitizes input events to prevent XSS attacks (debounced for performance)
   * Cleans HTML/script content from textarea and input fields (except email fields)
   */
  @HostListener('document:input', ['$event'])
  onInputEvent(event: Event): void {
    const target = event.target as HTMLTextAreaElement | HTMLInputElement;
    if (this.shouldSanitizeInput(target)) {
      // Push to debounced subject instead of immediate sanitization
      this.inputSanitize$.next({ target });
    }
  }

  /**
   * Sanitizes paste events to prevent malicious content injection
   * Allows email fields and embed links to preserve formatting
   */
  @HostListener('document:paste', ['$event'])
  onPasteEvent(event: ClipboardEvent): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (!event.clipboardData || this.shouldSkipPasteSanitization(target)) return;

    event.preventDefault();
    if (target && this.isTextInputElement(target)) {
      const raw = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain');
      const cleaned = DOMPurify.sanitize(raw, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      this.insertTextAtCursor(target, cleaned);
    }
  }

  /**
   * Prevents screenshot capture attempts using keyboard shortcuts
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.isBrowser && event.shiftKey && (event.metaKey || event.key === Fields.Meta)) {
      this.cms.showScreenshotProhibitedModal();
    }
  }

  private shouldSanitizeInput(target: HTMLElement): boolean {
    return target && 
           ((target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') && 
            (target as HTMLInputElement).type !== 'email' && 
            target.id !== 'embed_link');
  }

  private shouldSkipPasteSanitization(target: HTMLElement): boolean {
    return target && ((target as HTMLInputElement).type === 'email' || target.id === 'embed_link');
  }

  private isTextInputElement(target: HTMLElement): boolean {
    return target.tagName === 'TEXTAREA' || target.tagName === 'INPUT';
  }

  private insertTextAtCursor(target: HTMLInputElement | HTMLTextAreaElement, text: string): void {
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;
    const value = target.value;
    target.value = value.slice(0, start) + text + value.slice(end);
    target.selectionStart = target.selectionEnd = start + text.length;
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }

  constructor(
    private readonly router: Router,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly connectionService: ConnectionService,
    private readonly seoService: SeoService,
    readonly _hS: HistoryService,
    private readonly http: HttpClient,
    private readonly apiCallService: ApiCallService,
    private readonly firebaseService: FirebaseService,
    private readonly dataService: DataService,
    private readonly cms: CommonService,
    private readonly headerFooterStateService: HeaderFooterStateService,
    private readonly marketingPageStateService: MarketingPageStateService,
    private readonly promotionalMarketingCleanup: PromotionalMarketingCleanupService,
    private readonly indexedDbService: IndexedDbService,
    private readonly pwaService: PwaService,
    private readonly mbs: MobileBridgeService,
    public readonly platformService: PlatformService,
    private readonly ngZone: NgZone,
    private readonly canonicalService: CanonicalService,
    private readonly gtagService: GtagService,
    private readonly lazyCssLoader: LazyCssLoaderService,
    private readonly localeService: LocaleService
  ) {
    this.initializeEnvironment();
  }

  /**
   * Initializes the application environment and core features
   * Sets up browser detection, white label config, theming, and navigation
   */
  private initializeEnvironment(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isCoverPageShellRoute = isCoverPageShellPath(this.router.url);
    if (this.isCoverPageShellRoute) {
      this.applyWhiteLabelCoverPageShell(true);
    }
    this.initializeBrowserSpecificFeatures();

    if (environment.whiteLabelConfig) {
      this.handleWhiteLabelConfig();
    } else {
      this.clearWhiteLabelOfflineMetadata();
      this.seoService.initializeAppSeo();
      this.setSSRThemeVariables();
    }
    this.processReferentUserDetails();

    // Defer Firebase Remote Config and fingerprint to idle to avoid impacting LCP
    if (this.isBrowser) {
        setTimeout(() => {
      this.initializeFirebaseServices();
    });
    }
  }

  /**
   * Sets basic CSS variables for server-side rendering to prevent FOUC
   * This handles the case when API data isn't available during prerendering
   */
  private setSSRThemeVariables(customColors?: any): void {
    // Remove existing SSR styles
    const existingStyle = this.document.getElementById('ssr-theme-variables');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Use custom colors if provided, otherwise fallback to defaults
    const colors = customColors || {
      bgColor: '#191a1c',
      themeColor: Application.DEFAULT_THEME_COLOR,
      secondaryBgColor: '#000000',
      postTextColor: Application.DEFAULT_TEXT_COLOR,
      buttonTextColor: Application.DEFAULT_BUTTON_TEXT_COLOR,
      bgTextColor: Application.DEFAULT_TEXT_COLOR,
      backgroundImage: null
    };

    // For SSR, inject comprehensive CSS variables into the document head
    const style = this.document.createElement('style');
    style.id = 'ssr-theme-variables';
    style.textContent = `
      :root {
        --configBgColor: ${colors.bgColor || '#191a1c'};
        --configThemeColor: ${colors.themeColor || Application.DEFAULT_THEME_COLOR};
        --configSecondaryBgColor: ${colors.secondaryBgColor || '#000000'};
        --configPostTextColor: ${colors.postTextColor || Application.DEFAULT_TEXT_COLOR};
        --configButtonTextColor: ${colors.buttonTextColor || Application.DEFAULT_BUTTON_TEXT_COLOR};
        --configBgTextColor: ${colors.bgTextColor || Application.DEFAULT_TEXT_COLOR};
        --configBackgroundImage: ${colors.backgroundImage ? `url('${colors.backgroundImage}')` : 'none'};
        --theme-bg-primary: #272a2b;
        --theme-bg-secondary: #191a1c;
        --theme-bg-tertiary: #131415;
        --theme-bg-modal: #191a1c;
        --theme-bg-input: #424346;
        --theme-bg-card: #272a2b;
        --theme-bg-dark: #000;
        --theme-bg-light: #fff;
        --theme-bg-icon-light: #fff;
        --theme-bg-overlay-dark: rgba(0, 0, 0, 0.7);
        --theme-bg-overlay-dark-alt: rgba(44, 44, 44, 0);
        --theme-bg-footer: #002339;
        --theme-text-primary: #ffffff;
        --theme-text-secondary: #9f9f9f;
        --theme-text-placeholder: #9f9f9f;
        --theme-text-dark: #131415;
        --theme-text-disabled: rgba(0, 0, 0, 0.25);
        --theme-text-inverse: #000;
        --theme-interactive-link: #09c6da;
        --theme-interactive-hover: #333;
        --theme-interactive-focus: rgba(0, 182, 234, 0.3);
        --theme-interactive-button: #424346;
        --theme-interactive-accent: #00b6ea;
        --theme-border-primary: rgba(255, 255, 255, 0.16);
        --theme-border-secondary: #474748;
        --theme-border-transparent: transparent;
        --theme-gradient-primary: linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%);
        --theme-gradient-secondary: linear-gradient(to bottom, #002740 50%, #002740 100%);
        --theme-gradient-overlay-dark: linear-gradient(to top, #131415, rgba(44, 44, 44, 0) 100%, rgba(0, 0, 0, 0) 0%);
        --theme-gradient-overlay-light: linear-gradient(to top, #131415, rgba(155, 155, 155, 0) 33%, rgba(0, 0, 0, 0));
        --theme-gradient-sidebar-overlay: linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.6));
        --theme-gradient-profile-card: linear-gradient(to top, #131415, rgba(44, 44, 44, 0) 66%, rgba(0, 0, 0, 0) 34%);
        --theme-gradient-button-primary: linear-gradient(95deg, #1de9b6 0%, #00b6ea 100%);
        --theme-gradient-button-secondary: linear-gradient(to bottom, #002740 50%, #002740 100%);
        --theme-gradient-icon-text: linear-gradient(95deg, #fff 0%, #fff 100%);
        --theme-search-bg: #424346;
        --theme-search-bg-mobile: #013f5e;
        --theme-search-bg-desktop: #1a4158;
        --theme-message-strip: #424346;
        --theme-shadow-light: none;
      }
      body { 
        background-color: var(--configBgColor);
        color: var(--configPostTextColor);
        transition: background-color 0.3s ease, color 0.3s ease;
      }
    `;
    this.document.head.appendChild(style);
  }


  private initializeFirebaseServices(): void {
    this.firebaseService.generateFingerprint();
    this.firebaseService.getRemoteConfigValue();
  }

  /**
   * Handles white label configuration loading and caching
   * Uses CacheService for 6-hour cached responses
   */
  private handleWhiteLabelConfig(): void {
    const currentConfig = extractWhiteLabelConfig();
    if (currentConfig && isValidWhiteLabelConfig(currentConfig)) {
      this.processWhiteLabelConfig(currentConfig);
    }
  }

  /**
   * Processes white label configuration data and applies theming
   */
  private processWhiteLabelConfig(res: any): void {    
    // Check for preview parameters and use them if they exist
    if (this.isBrowser && this.document?.location) {
      const urlParams = new URLSearchParams(this.document.location.search);
      const isPreview = urlParams.get('preview') === 'true';
      
      if (isPreview) {
        // Create preview config object with parameter values
        const previewConfig: any = {};
        
        // Check each parameter and use it if it exists
        const params = ['logo_url', 'background_image', 'bg_color', 'bg_text_color', 'secondary_bg_color', 'button_text_color', 'post_text_color', 'theme_color', 'user_text', 'user_font'];
        
        params.forEach(param => {
          const value = urlParams.get(param);
          if (value) {
            previewConfig[param] = value;
          }
        });
        
        // If we have preview parameters, merge them with existing config
        if (Object.keys(previewConfig).length > 0) {
          this.authService.isWhiteLabelPreview = true;
          if (res.data?.whiteLabel) {
            Object.assign(res.data.whiteLabel, previewConfig);
          }
        }
      }
    }
    
    const logo = this.getWhiteLabelLogo(res);
    this.setConfigData(res);
    this.seoService.setWhiteLabelSeo(res.data, res.user, logo);
  }

  private getWhiteLabelLogo(res: any): string {
    const defaultLogo = '/assets/favicon/TopFans_180.png';
    if (res?.user?.profile_image_url) {
      return res.user.profile_image_url;
    }
    if (res?.data?.whiteLabel?.custom_logo && Object.keys(res.data.whiteLabel.custom_logo).length) {
      return res.data.whiteLabel.custom_logo[imageSize[144]]?.url || defaultLogo;
    }
    return res?.data?.whiteLabel?.logo_url || defaultLogo;
  }

  private processReferentUserDetails(): void {
    if (this.isBrowser && this.document?.location) {
      const urlParams = new URLSearchParams(this.document.location.search);
      const isReferent = urlParams.get('is_email') === 'true';
      if (isReferent) {
        const params = [
          'birth_date',
          'gender',
          'email',
          'first_name',
          'last_name',
          'general_location'
        ];
        params.forEach(param => {
          const value = urlParams.get(param);
          if (value) {
            this.cms.referentUserDetails[param] = value;
          }
        });
      }
    }
  }

  async ngOnInit(): Promise<void> {
    // Setup debounced input sanitization (Performance optimization - prevents 300ms+ input delays)
    this.inputSanitize$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(({ target }) => {
      const cleaned = DOMPurify.sanitize(target.value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      if (cleaned !== target.value) {
        target.value = cleaned;
      }
    });

    if (this.isBrowser) {
      this.gtagService.start();
      this.loadDeferredCss();
      await this.initializeBrowserServices();
      this.initializeServiceWorker();
    }
    this.subscribeToServices();
    this.updateSidebarVisibility();
  }

  private async initializeBrowserServices(): Promise<void> {
    this.mbs.forceSendMessage(MOBILE_BRIDGE_EVENT_SEND.RELOADED, {});

    const data = await this.indexedDbService.getAllItems();
    this.indexedDbService.setIndexedDb = data;

    this.pwaService.initPwaData();

    // Defer client IP fetch to idle after load; not needed for initial paint
    if (!this.dataService.getDeviceIp()) {
      setTimeout(() => {
        this.getSystemIPAddress();
      });
    }
  }

  private loadDeferredCss(): void {
    setTimeout(() => {
      this.lazyCssLoader.load('lazy-videojs', 'lazy-emoji-mart', 'lazy-flag-icons');
    }, 0);
  }


  private initializeServiceWorker(): void {
    if (!('serviceWorker' in navigator)) return;
    
    let reloaded = false;
    
    navigator.serviceWorker
      .register('/offline.service.js', { scope: '/' })
      .then(() => {
        if (!environment.production) {
          console.log('Service Worker Registered');
        }
      })
      .catch((error) => console.error('Service Worker registration failed:', error));

    // Handle offline page reload
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'RELOAD_PAGE' && !reloaded) {
        reloaded = true;
        location.reload();
      }
    });
  }

  private subscribeToServices(): void {
    this.connectionService.getConnectionStatus().subscribe();
    this.subscribeClearToasts();
    this.marketingPageStateService.isMarketingPageVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.isMarketingPageVisible = visible;
        this.showSidebar =
          this.showSidebarForRoute &&
          !visible &&
          !this.isSeoLandingPageVisible;
      });
    this.marketingPageStateService.isSeoLandingPageVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.isSeoLandingPageVisible = visible;
        this.showSidebar =
          this.showSidebarForRoute &&
          !this.isMarketingPageVisible &&
          !visible;
      });
    this.marketingPageStateService.isWhiteLabelCoverPageVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.isWhiteLabelCoverPageVisible = visible;
        this.updateCoverPageBodySpacing(visible);
        this.showSidebar =
          this.showSidebarForRoute &&
          !visible &&
          !this.isMarketingPageVisible &&
          !this.isSeoLandingPageVisible;
      });
  }

  private updateCoverPageBodySpacing(isCoverPageVisible: boolean): void {
    if (isCoverPageVisible) {
      this.renderer.setStyle(
        this.document.body,
        'padding-top',
        '0',
        RendererStyleFlags2.Important
      );
      return;
    }

    this.renderer.removeStyle(this.document.body, 'padding-top');
  }


  ngOnDestroy(): void {
    this.updateCoverPageBodySpacing(false);
    if (this.isEmbedWLSMode) {
      this.renderer.removeClass(this.document.body, 'theme-whiteLabel');
      this.renderer.removeStyle(this.document.body, 'has-background-image');
    }
    if (this.globalClickListener) {
      this.globalClickListener();
    }
    if (this.previewMessageHandler) {
      window.removeEventListener('message', this.previewMessageHandler);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeBrowserSpecificFeatures(): void {
    if (this.isBrowser) {
      this.renderer.addClass(this.document.body, 'hydrated');
    }
    this.setupRouterHandling();
    this.setupPreviewMessageListener();
  }

  /**
   * Sets up listener for preview messages from parent window (WLS appearance editor)
   * Allows real-time theme preview updates without iframe reload
   */
  private setupPreviewMessageListener(): void {
    if (!this.isBrowser) return;

    const urlParams = new URLSearchParams(window.location.search);
    this.isEmbedWLSMode = urlParams.get('embed') === 'true';

    if (!this.isEmbedWLSMode) return;

    this.cms.isWhiteLabelWeb = true;
    this.cms.applyWhiteLabelBodyThemeClass();

    // Apply initial colors from URL params on first load
    const initialColors = this.extractColorsFromUrlParams(urlParams);
    if (initialColors) {
      this.applyThemeColors(initialColors);
    }

    // Apply initial logo from URL params
    const logoUrl = urlParams.get('logo_url');
    if (logoUrl) {
      this.previewLogoUrl = logoUrl;
    }
    this.previewIsTextLogo = urlParams.get('is_text_logo') === 'true';

    this.previewMessageHandler = this.handlePreviewMessage.bind(this);
    window.addEventListener('message', this.previewMessageHandler);
  }

  /**
   * Extracts color data from URL parameters
   */
  private extractColorsFromUrlParams(urlParams: URLSearchParams): Record<string, string> | null {
    const colorData: Record<string, string> = {};
    const colorKeys = ['bg_color', 'bg_text_color', 'secondary_bg_color', 'post_text_color', 'theme_color', 'button_text_color', 'background_image'];

    colorKeys.forEach(key => {
      const value = urlParams.get(key);
      if (value) {
        colorData[key] = value;
      }
    });

    return Object.keys(colorData).length > 0 ? colorData : null;
  }

  /**
   * Applies theme colors to CSS variables
   * Used by both initial URL param colors and postMessage updates
   */
  private applyThemeColors(data: Record<string, string>): void {
    const elements = [this.document.documentElement, this.document.body];
    const cssVarMap: Record<string, string> = {
      bg_color: '--configBgColor',
      theme_color: '--configThemeColor',
      secondary_bg_color: '--configSecondaryBgColor',
      post_text_color: '--configPostTextColor',
      button_text_color: '--configButtonTextColor',
      bg_text_color: '--configBgTextColor'
    };

    Object.entries(cssVarMap).forEach(([dataKey, cssVar]) => {
      if (data[dataKey]) {
        elements.forEach(el => el.style.setProperty(cssVar, data[dataKey]));
      }
    });

    // Handle background image
    const bgImage = data.background_image ? `url('${data.background_image}')` : 'none';
    elements.forEach(el => el.style.setProperty('--configBackgroundImage', bgImage));

    if (data.background_image) {
      this.renderer.addClass(this.document.body, 'has-background-image');
    } else {
      this.renderer.removeClass(this.document.body, 'has-background-image');
    }
  }

  /**
   * Handles preview update messages from the WLS appearance editor
   */
  private handlePreviewMessage(event: MessageEvent): void {
    // Validate message type
    if (event.data?.type !== WLS_PREVIEW_MESSAGE_TYPE) return;

    const data = event.data.data;
    if (!data) return;

    this.ngZone.run(() => {
      this.applyThemeColors(data);
      this.previewLogoUrl = data.logo_url || '';
      this.previewIsTextLogo = data.is_text_logo === true;
    });
  }

  private setupRouterHandling(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.keepCoverPageShellDuringNavigation(event.url);
      }
      if (event instanceof NavigationEnd) {
        this.canonicalService.setCanonicalURL(event.urlAfterRedirects);
        this.handleNavigationEnd(event);
      }
      this.navigationInterceptor(event);
    });
  }

  private keepCoverPageShellDuringNavigation(url: string): void {
    const urlPath = url.split('?')[0].split('#')[0];
    this.isCoverPageShellRoute = isCoverPageShellPath(urlPath);
    if (this.isCoverPageShellRoute) {
      this.showSidebarForRoute = false;
      this.showSidebar = false;
      this.applyWhiteLabelCoverPageShell(true);
    }
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    if (this.isBrowser) {
      window.scrollTo(Application.ZERO, Application.ZERO);
      this.promotionalMarketingCleanup.clearIfStrayMarketingOutsidePublicHome(
        event.urlAfterRedirects
      );
      this.localeService.reapplyTranslation();
    }

    const shouldResetSearch = !event.urlAfterRedirects.includes('search-result');
    this.cms.setSearchReset(shouldResetSearch);
    this.cms.setGTag('page_view', event.urlAfterRedirects);
    this.updateSidebarVisibility();

    if (this.authService.isAuthenticated()) {
      if (!this.whitelabelDataFetched) {
        this.whitelabelDataFetched = true;
        this.cms.fetchCreatorWhitelabelData();
      }
    } else {
      this.whitelabelDataFetched = false;
    }

    this.scheduleWhiteLabelBodyThemeResync();
  }

  /**
   * Routed components (e.g. auth-landing, public pages) often add theme-dark in ngOnInit.
   * Run after the current turn so those run first, then strip standard themes when WL is active.
   */
  private scheduleWhiteLabelBodyThemeResync(): void {
    if (!this.isBrowser) {
      return;
    }
    if (!this.cms.isWhiteLabelWeb && !this.isEmbedWLSMode) {
      return;
    }
    if (this.marketingPageStateService.isMarketingPageVisible) {
      return;
    }
    setTimeout(() => {
      if (!this.cms.isWhiteLabelWeb && !this.isEmbedWLSMode) {
        return;
      }
      if (this.marketingPageStateService.isMarketingPageVisible) {
        return;
      }
      this.cms.applyWhiteLabelBodyThemeClass();
    }, 0);
  }

  navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      this.loadingDataImg = true;
      const targetPath = event.url.split('?')[0].split('#')[0];
      const currentPath = this.router.url.split('?')[0].split('#')[0];
      const isSamePageNavigation = targetPath === currentPath;
      if (!isSamePageNavigation) {
        this.headerFooterStateService.updateFooterState({ isFooterVisible: false });
      }

      if (this.authService.isAuthenticated()) {
        this.authService.setTokenFor2FA('');
        setTimeout(() => {
          this.getSystemIPAddress();
        });
      }
    }
    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      this.loadingDataImg = false;
      if (event instanceof NavigationEnd) {
        if (!(HISTORY_NOT_TO_INCLUDE.includes(event.urlAfterRedirects))) {
          this.authService.history.push(event.urlAfterRedirects);
          if (this.authService.history.length > 15) {
            this.authService.history.shift();
          }
        }
      }
    }
  }



  // Manifest is finalized during whitelabel build; runtime updates removed
  private persistWhiteLabelOfflineMetadata(
    whiteLabel: { user_text?: string } | undefined
  ): void {
    if (!this.isBrowser || !whiteLabel) return;
    const siteName = whiteLabel.user_text?.trim();
    if (siteName) {
      this.cms.setValueInocalStorage('whiteLabelSiteName', siteName);
    }
  }

  private clearWhiteLabelOfflineMetadata(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem('whiteLabelSiteName');
  }

  private setConfigData(res: any): void {
    this.authService.setIOSThemeMeta(res.data.whiteLabel.bg_color);
    this.cms.setWhiteLabelConfiguration(res);
    this.persistWhiteLabelOfflineMetadata(res.data?.whiteLabel);
    this.cms.applyWhiteLabelBodyThemeClass();

    const colors = this.getWhiteLabelColors(res.data.whiteLabel);

    if (colors.backgroundImage) {
      this.renderer.addClass(this.document.body, 'has-background-image');
    }

    // Apply theme colors (works for both SSR and browser)
    this.setSSRThemeVariables(colors);
  }

  /**
   * Method specifically for handling colors during prerendering
   * Call this method when you have API data available during prerender
   */
  public applyPrerenderTheme(apiColors: any): void {
    const colors = {
      bgColor: apiColors.bg_color || Application.DEFAULT_BG_COLOR,
      themeColor: apiColors.theme_color || Application.DEFAULT_THEME_COLOR,
      secondaryBgColor: apiColors.secondary_bg_color || Application.DEFAULT_SECONDARY_BG_COLOR,
      postTextColor: apiColors.post_text_color || Application.DEFAULT_TEXT_COLOR,
      buttonTextColor: apiColors.button_text_color || Application.DEFAULT_BUTTON_TEXT_COLOR,
      bgTextColor: apiColors.bg_text_color || Application.DEFAULT_TEXT_COLOR,
      backgroundImage: apiColors.background_image || null
    };

    // This will inject CSS that gets included in the prerendered HTML
    this.injectThemeStylesheet(colors);
    
    // Also set SSR variables
    this.setSSRThemeVariables(colors);
    
    this.cms.applyWhiteLabelBodyThemeClass();

    if (colors.backgroundImage) {
      this.renderer.addClass(this.document.body, 'has-background-image');
    }
  }

  private getWhiteLabelColors(whiteLabel: any) {
    return {
      bgColor: whiteLabel.bg_color || (Application.DEFAULT_BG_COLOR as string),
      themeColor: whiteLabel.theme_color || (Application.DEFAULT_THEME_COLOR as string),
      secondaryBgColor: whiteLabel.secondary_bg_color || (Application.DEFAULT_SECONDARY_BG_COLOR as string),
      postTextColor: whiteLabel.post_text_color || (Application.DEFAULT_TEXT_COLOR as string),
      buttonTextColor: whiteLabel.button_text_color || (Application.DEFAULT_BUTTON_TEXT_COLOR as string),
      bgTextColor: whiteLabel.bg_text_color || (Application.DEFAULT_TEXT_COLOR as string),
      backgroundImage: whiteLabel.background_image || null
    };
  }


  /**
   * Injects theme colors as CSS stylesheet - works in both SSR and browser
   * This is the most reliable method for prerendering
   */
  private injectThemeStylesheet(colors: any): void {
    // Remove existing theme stylesheet if it exists
    const existingStyle = this.document.getElementById('dynamic-theme-colors');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create new stylesheet with theme colors
    const style = this.document.createElement('style');
    style.id = 'dynamic-theme-colors';
    style.type = 'text/css';
    
    // CSS that will be included in prerendered HTML
    style.textContent = `
      :root {
        --configBgColor: ${colors.bgColor} !important;
        --configThemeColor: ${colors.themeColor} !important;
        --configSecondaryBgColor: ${colors.secondaryBgColor} !important;
        --configPostTextColor: ${colors.postTextColor} !important;
        --configButtonTextColor: ${colors.buttonTextColor} !important;
        --configBgTextColor: ${colors.bgTextColor} !important;
        --configBackgroundImage: ${colors.backgroundImage ? `url('${colors.backgroundImage}')` : 'none'} !important;
      }
      
      /* Direct CSS rules for immediate styling */
      body.theme-whiteLabel {
        background-color: ${colors.bgColor};
        color: ${colors.postTextColor};
      }
      
      .theme-color { background-color: ${colors.themeColor}; }
      .bg-color { background-color: ${colors.bgColor}; }
      .text-color { color: ${colors.postTextColor}; }
      .button-bg { background-color: ${colors.themeColor}; color: ${colors.buttonTextColor}; }
    `;
    
    // Insert at the beginning of head for higher priority
    this.document.head.insertBefore(style, this.document.head.firstChild);
  }


  onActivate(): void {
    if (this.isBrowser) {
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  }

  /**
   * Updates the sidebar visibility based on the current route
   * Sidebar should be shown for authenticated routes that need navigation
   */
  private updateSidebarVisibility(): void {

    if (this.isEmbedWLSMode) {
      this.showSidebarForRoute = false;
      this.showSidebar = false;
      return;
    }

    const url = this.router.url;
    const urlPath = url.split('?')[0].split('#')[0];

    this.isCoverPageShellRoute = isCoverPageShellPath(urlPath);
    // Published WLS cover home / public sub-page: full-bleed shell + no site header
    if (this.isCoverPageShellRoute) {
      this.showSidebarForRoute = false;
      this.showSidebar = false;
      this.applyWhiteLabelCoverPageShell(true);
      return;
    }

    if (
      this.isWhiteLabelCoverPageVisible &&
      !!environment.whiteLabelConfig &&
      environment.is_whitelabel_cover_page_published === true
    ) {
      this.applyWhiteLabelCoverPageShell(false);
    }

    // Routes that should NOT show sidebar
    const hideSidebarRoutes = [
      '/auth',
      '/privacy-policy',
      '/terms-conditions',
      '/contact-us',
      '/about',
      '/pricing',
      '/advertise',
      '/terms-of-service',
      '/help-faq',
      '/share',
      '/access-user',
      '/not-found',
      '/unsubscribe-instruction',
      '/unsubscribe/newsletter',
      '/imported/opt-out',
      '/shopify-connect',
      '/calendar-connect',
      '/order-status',
      '/bio',
      '/monetize/redeem-ticket',
      '/creator',
      '/creators',
      '/post/',
      '/wls/',
      '/home/',
      '/global/',
      '/edit-preference/whitelabel-cover-page',
      '/edit-preference/become-a-creator/',
      '/edit-preference/whitelabel/landing-builder',
      '/content/',
      '/request-demo',
      '/schedule-a-demo'
    ];
    const topFansOnlyHideSidebarRoutes = this.cms.isWhiteLabelWeb
      ? []
      : ['/edit-preference/whitelabel'];
    
    // Check if current route should hide sidebar
    this.showSidebarForRoute = !(
      [...hideSidebarRoutes, ...topFansOnlyHideSidebarRoutes].some(route => urlPath.startsWith(route)) ||
      (urlPath === '/' && !environment.whiteLabelConfig)
    );
    this.showSidebar =
      this.showSidebarForRoute &&
      !this.isMarketingPageVisible &&
      !this.isSeoLandingPageVisible &&
      !this.isWhiteLabelCoverPageVisible;
  }

  private applyWhiteLabelCoverPageShell(visible: boolean): void {
    if (this.isWhiteLabelCoverPageVisible !== visible) {
      this.isWhiteLabelCoverPageVisible = visible;
      this.marketingPageStateService.setWhiteLabelCoverPageVisible(visible);
    }
    this.updateCoverPageBodySpacing(visible);
    this.headerFooterStateService.updateState({ isHeaderVisible: !visible });
    this.headerFooterStateService.updateFooterState({
      isFooterVisible: !visible
    });
  }

  private subscribeClearToasts(): void {
    if (this.isBrowser) {
      this.globalClickListener = this.renderer.listen('document', 'click', () => {
        this.cms.clearToastMsgs();
      });
    }
  }

  private getSystemIPAddress(): void {
    const httpOptions = { headers: new HttpHeaders({}) };
    
    this.http.get(environment.IP_ENDPOINT, httpOptions)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const ipAddress = res?.ip || Application.ERROR_IP_ADDRESS;
          this.dataService.setDeviceIP(ipAddress);
          this.updateLastSession(ipAddress);
        },
        error: () => {
          this.dataService.setDeviceIP(Application.ERROR_IP_ADDRESS);
          this.updateLastSession(Application.ERROR_IP_ADDRESS);
        }
      });
  }

  private updateLastSession(ipAddress: string): void {
    if (!this.authService.isAuthenticated()) return;
    const payload = {
      lastSessionIP: ipAddress,
      lastSessionDevice: this.cms.getDeviceInfo()
    };
    this.apiCallService.post(API_URLS.sessionUpdate, payload, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
        error: (err) => console.error('Session update failed:', err)
      });
  }


}